import React, { useState, useMemo } from 'react';
import { Box, Flex, Text, Icon, Collapse, Spinner, useColorMode } from '@chakra-ui/react';
import RateSourceTooltip from '../shared/RateSourceTooltip';
import { FiChevronRight, FiChevronDown } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { cardStyle, cardTitle, viewAllStyle } from './dashboardStyles';
import UnconvertibleNote from '../shared/UnconvertibleNote';
import { categoryTint } from '../../utils/categoryTint';

const TOP_N_ALL = 3;
const TOP_N_SINGLE = 7;

function CategoryRow({ cat, maxSpent, isIncome, pct, onClick }) {
  const { colorMode } = useColorMode();
  // The count sits on a tint of the category's own colour, in a shade of it
  // that reads on that tint.
  const tint = categoryTint(cat.color || '#3B82F6', colorMode);
  const barWidth = maxSpent > 0 ? Math.min((cat.amount / maxSpent) * 100, 100) : 0;

  return (
    <Flex align="center" gap={3} minH="36px" cursor="pointer" _hover={{ bg: 'redesign.bg.hover' }} borderRadius="md" mx={-1} px={1} onClick={onClick}>
      <Flex w="24px" h="24px" borderRadius="7px" align="center" justify="center" flexShrink={0}
        bg={tint.bg} color={tint.fg} fontSize="2xs" fontWeight={600} textStyle="tabularNumbers">
        {cat.count > 0 ? cat.count : <Box w="7px" h="7px" borderRadius="2px" bg={tint.dot} />}
      </Flex>
      <Text fontSize="md" flexShrink={0}>{cat.icon || '❓'}</Text>
      <Text fontSize="md" fontWeight={500} color="redesign.heading" flex={1} isTruncated>{cat.category}</Text>
      <Text fontSize="sm" fontWeight={600} color="redesign.heading" textAlign="right" flexShrink={0} textStyle="tabularNumbers">
        ${cat.amount.toLocaleString('en-US', { minimumFractionDigits: cat.amount % 1 === 0 ? 0 : 2 })}
      </Text>
      <Box w="80px" h="6px" borderRadius="full" bg="redesign.bg.sunken" overflow="hidden" flexShrink={0}>
        <Box h="100%" borderRadius="full" bg={tint.dot} w={`${barWidth}%`} />
      </Box>
      {pct != null && (
        <Text fontSize="2xs" fontWeight={600} color={isIncome ? 'redesign.amount.income' : 'redesign.text.muted'} w="38px" textAlign="right" flexShrink={0} textStyle="tabularNumbers">
          {pct < 1 ? '<1' : pct.toFixed(1)}%
        </Text>
      )}
    </Flex>
  );
}

/**
 * Top Categories card — top N spending/income categories grouped by currency
 * with progress bars and % share badges. Supports drilldown navigation.
 */
export default function DashboardTopCategories({
  categoryBreakdown, expenseTotals, incomeTotals, selectedCurrency, onCategoryClick, onNavigate,
  // MI-298: when a display currency is set, the per-currency groups collapse into
  // one converted list (buckets already converted at each movement's own date).
  displayCurrency, convertedBreakdown, convertedExpenseTotal = 0, convertedIncomeTotal = 0,
  convertedUnconvertible = [], convertedNumUnconvertible = 0, convertedLoading = false,
  conversion = {},
}) {
  const { t } = useTranslation('dashboard');
  const [collapsedCurrencies, setCollapsedCurrencies] = useState({});
  const toggleCurrency = (cur) => setCollapsedCurrencies(p => ({ ...p, [cur]: !p[cur] }));

  const converting = Boolean(displayCurrency);
  const isAll = selectedCurrency === 'all';
  const allCurrencies = Object.keys(categoryBreakdown?.expenses || {});
  const hasMultipleCurrencies = isAll && allCurrencies.length > 1;
  const topN = hasMultipleCurrencies ? TOP_N_ALL : TOP_N_SINGLE;

  const expenseData = useMemo(() => {
    const data = categoryBreakdown?.expenses || {};
    const result = {};
    for (const [cur, cats] of Object.entries(data)) {
      result[cur] = cats.slice(0, topN);
    }
    return result;
  }, [categoryBreakdown, topN]);

  const incomeData = useMemo(() => {
    const data = categoryBreakdown?.income || {};
    const result = {};
    for (const [cur, cats] of Object.entries(data)) {
      result[cur] = cats.slice(0, topN);
    }
    return result;
  }, [categoryBreakdown, topN]);

  const currencies = isAll ? Object.keys(expenseData) : [selectedCurrency];
  const showHeaders = hasMultipleCurrencies;
  const allCats = [...Object.values(expenseData).flat(), ...Object.values(incomeData).flat()];
  const maxSpent = Math.max(...allCats.map(c => c.amount), 1);

  const singleCurrency = isAll && currencies.length === 1 ? currencies[0] : selectedCurrency;

  const renderRows = (cats, cur, isIncome) => {
    const total = isIncome ? (incomeTotals?.[cur] || 0) : (expenseTotals?.[cur] || 0);
    return cats.map(c => (
      <CategoryRow
        key={(isIncome ? 'inc-' : 'exp-') + (c.category_id || c.category)}
        cat={c}
        maxSpent={maxSpent}
        isIncome={isIncome}
        pct={total > 0 ? (c.amount / total) * 100 : null}
        onClick={() => onCategoryClick(c.category_id, c.category, cur)}
      />
    ));
  };

  // Converted (single-currency) view: one flat, top-N list. Bars scale to the
  // largest converted amount; % share uses the converted income/expense totals.
  // No currency is passed to the drilldown — the converted view spans currencies.
  const convExp = (convertedBreakdown?.expenses || []).slice(0, TOP_N_SINGLE);
  const convInc = (convertedBreakdown?.income || []).slice(0, TOP_N_SINGLE);
  const convMax = Math.max(...[...convExp, ...convInc].map(c => c.amount), 1);
  const convEmpty = convExp.length === 0 && convInc.length === 0;

  const renderConvertedRows = (cats, isIncome) => {
    const total = isIncome ? convertedIncomeTotal : convertedExpenseTotal;
    return cats.map(c => (
      <CategoryRow
        key={(isIncome ? 'inc-' : 'exp-') + (c.category_id || c.category)}
        cat={c}
        maxSpent={convMax}
        isIncome={isIncome}
        pct={total > 0 ? (c.amount / total) * 100 : null}
        onClick={() => onCategoryClick(c.category_id, c.category, null)}
      />
    ));
  };

  return (
    <Box {...cardStyle} minH="300px">
      <Flex justify="space-between" align="center" mb={4} gap={2}>
        <Flex align="center" gap={2} minW={0}>
          <Text {...cardTitle} minW={0}>{t('topCategories.title')}</Text>
          {converting && (
            <RateSourceTooltip
              label={t('common:convert.approxTooltip')}
              sources={conversion.rateSources}
              isDisabled={!conversion.isConverted}
            >
              <Text fontSize="xs" fontWeight={600} color="redesign.text.muted" bg="redesign.bg.card" px={2} py="2px" borderRadius="4px" flexShrink={0} cursor="default">
                {conversion.isConverted && '≈ '}{displayCurrency}
              </Text>
            </RateSourceTooltip>
          )}
        </Flex>
        <Text {...viewAllStyle} onClick={() => onNavigate('categories')} flexShrink={0}>{t('topCategories.viewAll')} <Icon as={FiChevronRight} w="14px" h="14px" /></Text>
      </Flex>
      {converting ? (
        convertedLoading && convEmpty ? (
          <Flex justify="center" align="center" py={8}>
            <Spinner size="sm" color="redesign.text.muted" />
          </Flex>
        ) : (
          <>
            <Flex direction="column" gap={2}>
              {renderConvertedRows(convExp, false)}
              {renderConvertedRows(convInc, true)}
            </Flex>
            {convEmpty && convertedNumUnconvertible === 0 && (
              <Text fontSize="md" color="redesign.text.muted" textAlign="center" py={4}>{t('topCategories.noData')}</Text>
            )}
            {convertedNumUnconvertible > 0 && (
              <Box mt={3}>
                <UnconvertibleNote buckets={convertedUnconvertible} count={convertedNumUnconvertible} />
              </Box>
            )}
          </>
        )
      ) : showHeaders ? (
        <Box>
          {currencies.map(cur => {
            const expCats = expenseData[cur] || [];
            const incCats = incomeData[cur] || [];
            return (
              <Box key={cur}>
                <Flex as="button" align="center" gap={2} py={2} w="100%" onClick={() => toggleCurrency(cur)}>
                  <Icon as={collapsedCurrencies[cur] ? FiChevronRight : FiChevronDown} w="12px" h="12px" color="redesign.text.muted" />
                  <Text fontSize="xs" fontWeight={600} color="redesign.text.muted" bg="redesign.bg.card" px={2} py="2px" borderRadius="4px">{cur}</Text>
                  <Box flex={1} h="1px" bg="redesign.border" />
                </Flex>
                <Collapse in={!collapsedCurrencies[cur]} animateOpacity>
                  <Flex direction="column" gap={2} mb={2}>
                    {renderRows(expCats, cur, false)}
                    {renderRows(incCats, cur, true)}
                  </Flex>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Flex direction="column" gap={2}>
          {renderRows(expenseData[singleCurrency] || [], singleCurrency, false)}
          {renderRows(incomeData[singleCurrency] || [], singleCurrency, true)}
        </Flex>
      )}
      {!converting && Object.keys(expenseData).length === 0 && Object.keys(incomeData).length === 0 && (
        <Text fontSize="md" color="redesign.text.muted" textAlign="center" py={4}>{t('topCategories.noData')}</Text>
      )}
    </Box>
  );
}
